import { Button } from '@/components/ui/button';

/**
 * Track CTA click - captures origin and pushes to dataLayer
 */
const trackCTAClick = (role) => {
  const origin_path = window.location.pathname;
  const origin_url = window.location.href;
  
  // Save to sessionStorage for form attribution
  sessionStorage.setItem('waitlist_origin_path', origin_path);
  sessionStorage.setItem('waitlist_origin_url', origin_url);
  sessionStorage.setItem('waitlist_cta_role', role);
  
  // Push dataLayer event for GTM
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'cta_click',
    role: role,
    origin_path: origin_path,
    origin_url: origin_url
  });
};

/**
 * WaitlistCTA - Standardized CTA button for waitlist signups
 * Rectangular shape with rounded corners, generous internal spacing
 * 
 * @param {string} variant - 'founder' or 'investor'
 * @param {string} theme - 'auto', 'dark', or 'light' - background the CTA sits on
 * @param {function} onClick - Click handler
 * @param {string} className - Additional classes
 * @param {string} size - 'default', 'lg', or 'sm'
 */
export default function WaitlistCTA({ 
  variant = 'founder', 
  theme = 'auto', 
  onClick, 
  className = '',
  size = 'default',
  testId
}) {
  const isFounder = variant === 'founder';
  
  // Text content
  const roleLabel = isFounder ? 'Founder / Co-founder' : 'Investor';
  const actionLabel = isFounder ? 'Join the waitlist' : 'Request early access';
  
  // Determine role for tracking
  const trackingRole = isFounder ? 'founder' : 'investor';
  
  // Size classes - compact matching reference
  const sizeClasses = {
    sm: 'px-5 py-2.5 min-w-[140px]',
    default: 'px-6 py-3 min-w-[180px]',
    lg: 'px-8 py-4 min-w-[200px]'
  };
  
  // Text sizes - tight hierarchy like reference
  const textSizes = {
    sm: { primary: 'text-sm', secondary: 'text-xs' },
    default: { primary: 'text-base', secondary: 'text-sm' },
    lg: { primary: 'text-lg', secondary: 'text-sm' }
  };

  // Base styles - soft rounded rectangle like reference image
  const baseStyles = `
    rounded-3xl
    flex flex-col items-center justify-center 
    transition-all duration-200
    ${sizeClasses[size] || sizeClasses.default}
  `;

  // Primary CTA (Founder) - Filled blue
  const primaryStyles = `
    bg-blue-500 hover:bg-blue-600 
    text-white 
    border-0
    shadow-lg hover:shadow-xl
  `;

  // Secondary CTA (Investor) - Dark blue filled
  const secondaryStyles = `
    bg-slate-800 hover:bg-slate-700 
    text-white 
    border-0
    shadow-lg hover:shadow-xl
  `;

  const buttonStyles = isFounder ? primaryStyles : secondaryStyles;

  const handleClick = (e) => {
    // Track CTA click
    trackCTAClick(trackingRole);
    
    // Call original onClick handler
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Button
      onClick={handleClick}
      className={`${baseStyles} ${buttonStyles} ${className} h-auto`}
      data-testid={testId}
    >
      <span className={`font-semibold ${textSizes[size]?.primary || 'text-base'} leading-none`}>
        {roleLabel}
      </span>
      <span className={`${textSizes[size]?.secondary || 'text-sm'} opacity-85 mt-0.5 leading-none font-normal`}>
        {actionLabel}
      </span>
    </Button>
  );
}

/**
 * WaitlistCTAGroup - Renders both CTAs side by side (responsive)
 */
export function WaitlistCTAGroup({ 
  onFounderClick, 
  onInvestorClick, 
  theme = 'auto',
  size = 'default',
  className = '' 
}) {
  return (
    <div className={`flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-4 ${className}`}>
      <WaitlistCTA 
        variant="founder" 
        theme={theme}
        size={size}
        onClick={onFounderClick}
        testId="cta-founder"
      />
      <WaitlistCTA 
        variant="investor" 
        theme={theme}
        size={size}
        onClick={onInvestorClick}
        testId="cta-investor"
      />
    </div>
  );
}
